/**
 * Small hand-drawn marks — a pencil, an apple, a paperclip.
 *
 * Line art rather than emoji: emoji render differently on every machine and
 * bring their own colours, which would fight the palette. These take whatever
 * colour they are given and stay part of the room.
 */
export type DoodleName =
  | 'pencil'
  | 'apple'
  | 'calendar'
  | 'checklist'
  | 'star'
  | 'chart'
  | 'bookmark'
  | 'paperclip'
  | 'ruler'
  | 'coffee'
  | 'crayons'
  | 'sun'
  | 'flower'
  | 'leaf'
  | 'heart'
  | 'moon'
  | 'cloud'
  | 'apple-core'
  | 'globe'
  | 'bus'
  | 'clock'
  | 'lightbulb'
  | 'palette'
  | 'scissors'
  | 'notebook'
  | 'backpack'
  | 'mug'
  | 'apple-tree'
  | 'balloon'
  | 'kite'
  | 'rainbow'
  | 'snowflake'
  | 'apple-basket'
  | 'butterfly'
  | 'cat'
  | 'bee';

const PATHS: Record<DoodleName, string> = {
  // A pencil on the diagonal, with its nib and its band.
  pencil: 'M4 20l1-4L16 5l3 3L8 19l-4 1zM14 7l3 3',
  apple:
    'M12 8c-3 0-5 2-5 5.5S9.5 20 12 20s5-3 5-6.5S15 8 12 8zM12 8V5m0 0c0-1 1-2 2.5-2M12 5c0-1-1-1.5-2-1.5',
  calendar: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
  checklist: 'M4 7h10M4 12h10M4 17h10M17.5 6l1.5 1.5L22 4M17.5 16l1.5 1.5L22 14',
  star: 'M12 4l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.6-4.8 2.6.9-5.4L4.2 9.7l5.4-.8z',
  chart: 'M4 20V10M10 20V5M16 20v-7M22 20H2',
  bookmark: 'M6 3h12v18l-6-4-6 4z',
  paperclip: 'M17 8v9a5 5 0 0 1-10 0V7a3 3 0 0 1 6 0v9a1 1 0 0 1-2 0V8',
  ruler: 'M3 9h18v6H3zM7 9v3M11 9v3M15 9v3M19 9v3',
  coffee: 'M4 8h12v6a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4zM16 9h2a2 2 0 0 1 0 4h-2M6 3v2M10 3v2M14 3v2',
  crayons: 'M5 21V10l2-4 2 4v11zM15 21V10l2-4 2 4v11zM5 13h4M15 13h4',
  sun: 'M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10zM12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5L19 19M19 5l-1.5 1.5M6.5 17.5L5 19',

  /* A wider set, mostly so the mark beside a teacher's name can be her own. */
  flower:
    'M12 10.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zM12 10.5V7a2 2 0 1 1 0 3.5zM13.5 12H17a2 2 0 1 0-3.5 0zM12 13.5V17a2 2 0 1 0 0-3.5zM10.5 12H7a2 2 0 1 1 3.5 0zM12 17v5M10 20h4',
  leaf: 'M4 20C4 11 10 5 20 4c1 10-5 16-14 16zM8 16c2-3 5-6 9-8',
  heart: 'M12 20S4 14.5 4 9.2A4.2 4.2 0 0 1 12 7a4.2 4.2 0 0 1 8 2.2C20 14.5 12 20 12 20z',
  moon: 'M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z',
  cloud: 'M7 18h10a4 4 0 0 0 .5-8 5.5 5.5 0 0 0-10.6 1.4A3.4 3.4 0 0 0 7 18z',
  'apple-core':
    'M9 4c1 1 2 1.5 3 1.5S14 5 15 4M12 5.5v3M8 12c0-2 1.5-3.5 4-3.5s4 1.5 4 3.5c0 1.5-1 2-1 3.5s1 2 1 3.5c0 1.5-1.5 2-4 2s-4-.5-4-2c0-1.5 1-2 1-3.5s-1-2-1-3.5z',
  globe:
    'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM3 12h18M12 3c2.5 2.5 3.8 5.6 3.8 9s-1.3 6.5-3.8 9c-2.5-2.5-3.8-5.6-3.8-9S9.5 5.5 12 3z',
  bus: 'M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v9H4zM4 15h16M7 15v3M17 15v3M4 9h16M9 4v5',
  clock: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 7v5l3.5 2',
  lightbulb:
    'M9.5 18h5M10 21h4M8 12a4 4 0 1 1 8 0c0 1.6-1 2.4-1.4 3.4-.2.5-.3 1-.3 1.6h-4.6c0-.6-.1-1.1-.3-1.6C9 14.4 8 13.6 8 12z',
  palette:
    'M12 3a9 9 0 0 0 0 18c1.1 0 1.6-.8 1.4-1.6-.3-1.2.6-2.4 1.9-2.4H17a4 4 0 0 0 4-4c0-5-4-10-9-10zM8 9.5h.01M11.5 7h.01M15.5 8.5h.01M7.5 13.5h.01',
  scissors: 'M7 6l10 12M17 6L7 18M6.5 18.5a2 2 0 1 0 0 .01M6.5 5.5a2 2 0 1 0 0 .01',
  notebook:
    'M6 3h12a1 1 0 0 1 1 1v17H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zM4 8h3M4 12h3M4 16h3M11 8h5M11 12h5',
  backpack: 'M6 9a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v11H6zM9 5V4a3 3 0 0 1 6 0v1M9 13h6v3H9z',
  mug: 'M5 7h11v8a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4zM16 9h2a2.5 2.5 0 0 1 0 5h-2M7 3v2M10 3v2M13 3v2',
  'apple-tree':
    'M12 21v-6M8 15c-3 0-4.5-2-4.5-4.5S5 6 7.5 6C8 3.5 10 2.5 12 2.5S16 3.5 16.5 6c2.5 0 4.5 2 4.5 4.5S19 15 16 15z',
  balloon: 'M12 15c3 0 5-2.7 5-6s-2-6-5-6-5 2.7-5 6 2 6 5 6zM12 15l-1 2h2l-1-2M12 17c0 2 2 2 2 4',
  kite: 'M12 2l7 7-7 7-7-7zM12 2v14M5 9h14M12 16c0 3-2 3-2 6',
  rainbow: 'M3 19a9 9 0 0 1 18 0M6.5 19a5.5 5.5 0 0 1 11 0M10 19a2 2 0 0 1 4 0',
  snowflake: 'M12 2v20M3.5 7l17 10M20.5 7l-17 10M12 6l-2.5-2M12 6l2.5-2M12 18l-2.5 2M12 18l2.5 2',
  'apple-basket':
    'M4 10h16l-1.5 9a2 2 0 0 1-2 1.7H7.5a2 2 0 0 1-2-1.7zM7 10a5 5 0 0 1 10 0M9 14v3M12 14v3M15 14v3',
  butterfly:
    'M12 6v12M12 8c-1.5-3-6-4-7-1.5S7 13 12 12c5 1 8-1 7-5.5S13.5 5 12 8zM12 12c-1.5 3-5.5 4-6.5 1.5S8 17 12 18c4-1 7.5-2 6.5-4.5S13.5 15 12 12z',
  cat: 'M5 10.5 6 5l3.5 2.5h5L18 5l1 5.5v4a7 7 0 0 1-14 0zM9.5 12h.01M14.5 12h.01M12 15l-1.5 1M12 15l1.5 1M12 15v1.5',
  bee: 'M12 8a4 4 0 0 1 4 4v3a4 4 0 0 1-8 0v-3a4 4 0 0 1 4-4zM8 12h8M8 15h8M12 8V6M10.5 4.5 12 6l1.5-1.5M6 9 3.5 7M18 9l2.5-2',
};

/** The mark a teacher gets before she has picked one. */
export const DEFAULT_AVATAR_MARK: DoodleName = 'apple';

export const DOODLE_NAMES = Object.keys(PATHS) as DoodleName[];

/** Narrows a stored string, so an old or misspelt name can't blank the icon. */
export function isDoodle(name: string | null | undefined): name is DoodleName {
  return !!name && name in PATHS;
}

export function Doodle({
  name,
  size = 18,
  className = '',
  style,
}: {
  name: DoodleName;
  size?: number;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}
      style={style}
    >
      <path d={PATHS[name]} />
    </svg>
  );
}

/**
 * The faint mark in the corner of an empty card.
 *
 * An empty card is the most common thing a teacher sees on a Monday morning,
 * and a bare sentence on white makes the app feel broken rather than quiet.
 */
export function EmptyMark({ name, color }: { name: DoodleName; color: string }) {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute bottom-3 right-3 opacity-[0.12]"
      style={{ color }}
    >
      <Doodle name={name} size={56} />
    </span>
  );
}
